import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ExchangeRateService } from '../../services/exchange-rate.service';

import { ClientService } from '../../services/client.service';
import { ProductService } from '../../services/product.service';
import { ServiceService } from '../../services/service.service';
import { SaleService } from '../../services/sale.service';

import { SaleRequest } from '../../dto/sale.request';
import { SaleItemRequest } from '../../dto/sale-item.request';
import { SaleInstallmentRequest } from '../../dto/sale-installment.request';
import { ClientResponse } from '../../dto/client.response';
import { ProductResponse } from '../../dto/product.response';
import { ServiceResponse } from '../../dto/service.response';
import { SaleResponse } from '../../dto/sale.response';
import { ModalComponent } from "../../shared/components/ui/modal/modal.component";
import { FileInputComponent } from "../../shared/components/form/input/file-input.component";
import { LabelComponent } from "../../shared/components/form/label/label.component";
import { InputFieldComponent } from "../../shared/components/form/input/input-field.component";
import { OtInputComponent } from '../../shared/components/form/ot-input/ot-input.component';
import { SelectComponent } from "../../shared/components/form/select/select.component";
import { NotificationService } from '../../shared/components/ui/notification/notification.service';
import { DocumentSeriesService } from '../../services/document-series.service';
import { DetractionCodeService } from '../../services/detraction-code.service';
import { DetractionCodeResponse } from '../../dto/detraction-code.response';
import { DatePickerComponent } from '../../shared/components/form/date-picker/date-picker.component';
import { Option } from '../../shared/components/form/select/select.component';
import { QuickClientRegisterComponent } from './quick-client-register.component';
import { QuickProductRegisterComponent } from './quick-product-register.component';
import { QuickServiceRegisterComponent } from './quick-service-register.component';

interface SaleItemForm extends SaleItemRequest {
    _detractionId?: number;
    _detractionCode?: string;
    _detractionDescription?: string;
    _detractionPercentage?: number;
    _detractionMinAmount?: number;
}

@Component({
    selector: 'app-sale-register',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DecimalPipe,
        ModalComponent,
        FileInputComponent,
        LabelComponent,
        InputFieldComponent,
        OtInputComponent,
        SelectComponent,
        DatePickerComponent,
        QuickClientRegisterComponent,
        QuickProductRegisterComponent,
        QuickServiceRegisterComponent,
    ],
    templateUrl: './sale-register.component.html',
})
export class SaleRegisterComponent implements OnChanges {

    // =============================
    // INPUTS / OUTPUTS
    // =============================
    @Input() isOpen = false;
    @Input() selectedSale?: SaleResponse;
    @Output() onClose = new EventEmitter<void>();
    @Output() onSaved = new EventEmitter<void>();

    // =============================
    // CLIENTE
    // =============================
    selectedClient?: ClientResponse;
    selectedClientAddressId: number | null = null;
    clientAddress = '';
    clientSearchTerm = '';
    allClients: ClientResponse[] = [];
    clientResults: ClientResponse[] = [];
    showClientModal = false;
    loadingClients = false;
    clientCurrentPage = 1;
    readonly clientPageSize = 5;

    get pagedClientResults(): ClientResponse[] {
        const start = (this.clientCurrentPage - 1) * this.clientPageSize;
        return this.clientResults.slice(start, start + this.clientPageSize);
    }

    get clientTotalPages(): number {
        return Math.max(1, Math.ceil(this.clientResults.length / this.clientPageSize));
    }

    get clientPageEnd(): number {
        return Math.min(this.clientCurrentPage * this.clientPageSize, this.clientResults.length);
    }

    // =============================
    // PRODUCTOS
    // =============================
    productSearchTerm = '';
    allProducts: ProductResponse[] = [];
    productResults: ProductResponse[] = [];
    showProductModal = false;
    loadingProducts = false;
    productCurrentPage = 1;
    readonly productPageSize = 5;

    get pagedProductResults(): ProductResponse[] {
        const start = (this.productCurrentPage - 1) * this.productPageSize;
        return this.productResults.slice(start, start + this.productPageSize);
    }
    get productTotalPages(): number {
        return Math.max(1, Math.ceil(this.productResults.length / this.productPageSize));
    }
    get productPageEnd(): number {
        return Math.min(this.productCurrentPage * this.productPageSize, this.productResults.length);
    }

    // =============================
    // SERVICIOS
    // =============================
    serviceSearchTerm = '';
    allServices: ServiceResponse[] = [];
    serviceResults: ServiceResponse[] = [];
    showServiceModal = false;
    loadingServices = false;
    serviceCurrentPage = 1;
    readonly servicePageSize = 5;

    get pagedServiceResults(): ServiceResponse[] {
        const start = (this.serviceCurrentPage - 1) * this.servicePageSize;
        return this.serviceResults.slice(start, start + this.servicePageSize);
    }
    get serviceTotalPages(): number {
        return Math.max(1, Math.ceil(this.serviceResults.length / this.servicePageSize));
    }
    get servicePageEnd(): number {
        return Math.min(this.serviceCurrentPage * this.servicePageSize, this.serviceResults.length);
    }

    // =============================
    // SERVICIO RÁPIDO
    // =============================
    showQuickServiceModal = false;
    quickServiceName = '';
    quickServiceQuantity: number = 1;

    // =============================
    // REGISTRO RÁPIDO (maestros)
    // =============================
    showQuickClientRegister = false;
    showQuickProductRegister = false;
    showQuickServiceRegister = false;

    onClientCreated(client: ClientResponse): void {
        this.showQuickClientRegister = false;
        this.showClientModal = false;
        this.allClients = [client, ...this.allClients];
        this.selectClient(client);
    }

    onProductCreated(product: ProductResponse): void {
        this.showQuickProductRegister = false;
        this.showProductModal = false;
        this.allProducts = [product, ...this.allProducts];
        this.addProduct(product);
    }

    onServiceCreated(service: ServiceResponse): void {
        this.showQuickServiceRegister = false;
        this.showServiceModal = false;
        this.allServices = [service, ...this.allServices];
        this.addService(service);
    }
    quickServicePrice: number = 0;
    quickServiceDiscount: number = 0;
    quickServiceDetractionId = '';

    // =============================
    // DETRACCIÓN
    // =============================
    detractionCodeOptions: Option[] = [];
    loadingDetractionCodes = false;
    allDetractionCodes: DetractionCodeResponse[] = [];

    // =============================
    // MONEDA / TIPO DE PAGO
    // =============================
    readonly tomorrowStr: string = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    })();
    currencyCode: 'PEN' | 'USD' = 'PEN';
    paymentType: 'CONTADO' | 'CREDITO' = 'CONTADO';

    get currencySymbol(): string {
        const code = this.isViewMode
            ? (this.selectedSale?.currencyCode ?? 'PEN')
            : this.currencyCode;
        return code === 'USD' ? '$' : 'S/';
    }

    // =============================
    // ITEMS
    // =============================
    items: SaleItemForm[] = [];

    // =============================
    // PAGOS
    // =============================
    payments: {
        paymentMethodId: string;
        amountPaid: number;
        paymentReference: string;
        file?: File;
    }[] = [];

    // =============================
    // CUOTAS (crédito)
    // =============================
    installments: SaleInstallmentRequest[] = [];

    // =============================
    // DATOS ADICIONALES
    // =============================
    purchaseOrder = '';
    observations = '';
    otNumber = '';
    relatedGuides: string[] = [];

    addRelatedGuide() { this.relatedGuides.push(''); }
    removeRelatedGuide(i: number) { this.relatedGuides.splice(i, 1); }

    paymentMethodOptions: { value: string; label: string }[] = [
        { value: '6', label: 'Efectivo' },
        { value: '7', label: 'Transferencia' },
        { value: '8', label: 'Tarjeta' },
        { value: '9', label: 'Yape/Plin' }
    ];

    // =============================
    // COMPROBANTE
    // =============================
    documentTypeCode: string = '03'; // 03 = BOLETA (default)
    documentSeriesId: number = 0;

    documentSeries?: string;
    documentSequence?: number;
    loadingSeries = false;
    documentTypeLocked = false;

    // =============================
    // MODO VISTA
    // =============================
    isViewMode = false;
    loadingDetail = false;

    isSubmitting = false;
    isGeneratingQuotation = false;

    // TIPO DE CAMBIO
    exchangeRateSale = 0;

    constructor(
        private clientService: ClientService,
        private productService: ProductService,
        private serviceService: ServiceService,
        private saleService: SaleService,
        private documentSeriesService: DocumentSeriesService,
        private detractionCodeService: DetractionCodeService,
        private exchangeRateService: ExchangeRateService,
        private notify: NotificationService
    ) { }

    private loadExchangeRate(): void {
        this.exchangeRateService.getToday().subscribe({
            next: res => { this.exchangeRateSale = res.data?.sale ?? 0; },
            error: () => { this.exchangeRateSale = 0; }
        });
    }


    previewCorrelative(typeCode: string) {

        this.loadingSeries = true;

        this.documentSeriesService.getNextSequence(typeCode)
            .subscribe({
                next: res => {

                    this.documentSeriesId = res.data?.id ?? 0;
                    this.documentSeries = res.data?.series;
                    this.documentSequence = res.data?.sequence;

                    this.loadingSeries = false;
                },
                error: err => {
                    this.notify.error(
                        err?.error?.message ?? 'No se pudo obtener correlativo'
                    );
                    this.loadingSeries = false;
                }
            });
    }

    private applyDocumentTypeByClient(): void {

        if (!this.selectedClient) {
            this.documentTypeLocked = false;
            return;
        }

        if (Number(this.selectedClient.documentTypeId) === 2) {
            // RUC → FACTURA
            this.documentTypeCode = '01';
        } else {
            // DNI u otro → BOLETA
            this.documentTypeCode = '03';
        }

        this.documentTypeLocked = true;

        this.previewCorrelative(this.documentTypeCode);
    }

    onDocumentTypeChange() {
        if (!this.documentTypeCode) return;
        this.previewCorrelative(this.documentTypeCode);
    }

    get formattedDocument(): string {

        if (this.loadingSeries) {
            return 'Cargando...';
        }

        if (!this.documentSeries || !this.documentSequence) {
            return '';
        }

        return `${this.documentSeries}-${this.documentSequence
            .toString()
            .padStart(8, '0')}`;
    }

    // =========================================================
    // LIFECYCLE
    // =========================================================

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen'] && this.isOpen) {
            this.loadExchangeRate();
        }
        if (changes['selectedSale']) {
            if (this.selectedSale && this.isOpen) {
                this.loadSaleForEdit(this.selectedSale);
            } else if (!this.selectedSale) {
                this.isViewMode = false;
            }
        } else if (changes['isOpen'] && this.isOpen && !this.selectedSale) {
            this.resetSale();

            this.previewCorrelative(this.documentTypeCode);
        }
    }

    private loadSaleForEdit(sale: SaleResponse): void {
        this.resetSale();
        this.isViewMode = sale.saleStatus !== 'BORRADOR';

        // Datos adicionales
        this.purchaseOrder = sale.purchaseOrder ?? '';
        this.observations = sale.observations ?? '';
        this.otNumber = sale.otNumber ?? '';
        this.relatedGuides = [...(sale.relatedGuides ?? [])];

        // Mapear items
        this.items = sale.items.map(i => ({
            itemType: i.itemType as 'PRODUCTO' | 'SERVICIO' | 'PERSONALIZADO',
            productId: i.productId,
            serviceId: i.serviceId,
            description: i.description,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
            discountPercentage: i.discountPercentage
        }));

        // Cargar cliente completo por ID
        this.loadingDetail = true;
        this.clientService.getAll({ id: sale.client.id }).subscribe({
            next: res => {
                this.selectedClient = res.data?.[0];
                this.applyDocumentTypeByClient();
                this.loadingDetail = false;
            },
            error: () => {
                // Fallback: construir cliente mínimo con los datos disponibles
                this.selectedClient = {
                    id: sale.client.id,
                    businessName: sale.client.businessName ? sale.client.businessName : sale.client.firstName + " " + sale.client.lastName,
                    documentType: '', documentNumber: '',
                    firstName: '', lastName: ''
                } as ClientResponse;
                this.loadingDetail = false;
            }
        });
    }

    // =========================================================
    // CLIENTES
    // =========================================================

    loadAllClients() {
        this.loadingClients = true;
        this.clientService.getAll({ status: 1 }).subscribe({
            next: res => {
                this.allClients = res.data ?? [];
                this.clientResults = this.allClients;
                this.loadingClients = false;
            },
            error: () => {
                this.allClients = [];
                this.clientResults = [];
                this.loadingClients = false;
            }
        });
    }

    searchClients() {
        const term = this.clientSearchTerm.trim().toLowerCase();

        if (!term) {
            this.clientResults = this.allClients;
        } else {
            // Filtrar en frontend por documento, nombre o razón social
            this.clientResults = this.allClients.filter(client => {
                const fullName = this.getClientFullName(client).toLowerCase();
                const doc = client.documentNumber.toLowerCase();
                const businessName = (client.businessName || '').toLowerCase();

                return doc.includes(term) || fullName.includes(term) || businessName.includes(term);
            });
        }

        // Reset a la primera página cuando cambia la búsqueda
        this.clientCurrentPage = 1;
    }

    getClientFullName(client: ClientResponse): string {
        if (client.firstName) {
            return `${client.firstName} ${client.lastName}`.trim();
        }
        return client.businessName || '';
    }

    getClientDisplay(client: ClientResponse): string {
        const name = this.getClientFullName(client);
        return `${client.documentType} - ${name}`;
    }

    openClientModal() {
        this.showClientModal = true;
        this.clientSearchTerm = '';
        this.clientCurrentPage = 1;
        if (this.allClients.length === 0) {
            this.loadAllClients();
        } else {
            this.clientResults = this.allClients;
        }
    }

    clientPrevPage() {
        if (this.clientCurrentPage > 1) this.clientCurrentPage--;
    }

    clientNextPage() {
        if (this.clientCurrentPage < this.clientTotalPages) this.clientCurrentPage++;
    }

    selectClient(client: ClientResponse) {
        const changing = this.selectedClient?.id !== client.id;
        this.selectedClient = client;
        this.showClientModal = false;
        this.clientSearchTerm = '';

        if (changing) {
            this.selectedClientAddressId = null;
            this.clientAddress = '';
            // Auto-select first registered address if available
            const firstAddr = client.addresses?.[0];
            if (firstAddr) {
                this.selectedClientAddressId = firstAddr.id;
                this.clientAddress = firstAddr.address;
            }
        }

        this.applyDocumentTypeByClient();
    }

    selectClientAddress(addressId: number, addressText: string): void {
        this.selectedClientAddressId = addressId;
        this.clientAddress = addressText;
    }

    onClientAddressInputChange(newValue: string): void {
        // If the user typed something different from the currently selected registered address, deselect it
        if (this.selectedClientAddressId !== null) {
            const registered = this.selectedClient?.addresses?.find(a => a.id === this.selectedClientAddressId);
            if (registered && newValue !== registered.address) {
                this.selectedClientAddressId = null;
            }
        }
    }

    clearClient() {
        this.selectedClient = undefined;
        this.selectedClientAddressId = null;
        this.clientAddress = '';
    }

    // =========================================================
    // PRODUCTOS
    // =========================================================

    openProductModal() {
        this.showProductModal = true;
        this.productSearchTerm = '';
        this.productCurrentPage = 1;
        if (this.allProducts.length === 0) {
            this.loadAllProducts();
        } else {
            this.productResults = this.allProducts;
        }
    }

    loadAllProducts() {
        this.loadingProducts = true;
        this.productService.getAll({ status: 1, currency: this.currencyCode }).subscribe({
            next: res => {
                this.allProducts = res.data ?? [];
                this.productResults = this.allProducts;
                this.loadingProducts = false;
            },
            error: () => {
                this.allProducts = [];
                this.productResults = [];
                this.loadingProducts = false;
            }
        });
    }

    onCurrencyChange() {
        this.items = [];
        this.allProducts = [];
        this.allServices = [];
    }

    searchProducts() {
        const term = this.productSearchTerm.trim().toLowerCase();
        if (!term) {
            this.productResults = this.allProducts;
        } else {
            this.productResults = this.allProducts.filter(p =>
                p.sku.toLowerCase().includes(term) ||
                p.name.toLowerCase().includes(term) ||
                (p.categoryName || '').toLowerCase().includes(term)
            );
        }
        this.productCurrentPage = 1;
    }

    addProduct(product: ProductResponse) {
        if (!this.checkDetractionCompatibility(product.detractionId)) {
            this.showProductModal = false;
            return;
        }
        const price = this.currencyCode === 'USD'
            ? (product.salePriceUsd ?? 0)
            : (product.salePricePen ?? 0);
        this.items.push({
            itemType: 'PRODUCTO',
            productId: product.id,
            description: product.name,
            quantity: 1,
            unitPrice: price,
            _detractionId: product.detractionId ?? undefined,
            _detractionCode: product.detractionCode ?? undefined,
            _detractionDescription: product.detractionDescription ?? undefined,
            _detractionPercentage: product.detractionPercentage ?? undefined,
            _detractionMinAmount: product.detractionMinAmount ?? undefined,
        });
        this.showProductModal = false;
        this.notify.success(`"${product.name}" agregado`, 'Producto agregado', 2500);
    }

    productPrevPage() { if (this.productCurrentPage > 1) this.productCurrentPage--; }
    productNextPage() { if (this.productCurrentPage < this.productTotalPages) this.productCurrentPage++; }

    // =========================================================
    // SERVICIOS
    // =========================================================

    openServiceModal() {
        this.showServiceModal = true;
        this.serviceSearchTerm = '';
        this.serviceCurrentPage = 1;
        if (this.allServices.length === 0) {
            this.loadAllServices();
        } else {
            this.serviceResults = this.allServices;
        }
    }

    loadAllServices() {
        this.loadingServices = true;
        this.serviceService.getAll({ status: 1, currencyCode: this.currencyCode }).subscribe({
            next: res => {
                this.allServices = res.data ?? [];
                this.serviceResults = this.allServices;
                this.loadingServices = false;
            },
            error: () => {
                this.allServices = [];
                this.serviceResults = [];
                this.loadingServices = false;
            }
        });
    }

    searchServices() {
        const term = this.serviceSearchTerm.trim().toLowerCase();
        if (!term) {
            this.serviceResults = this.allServices;
        } else {
            this.serviceResults = this.allServices.filter(s =>
                s.sku.toLowerCase().includes(term) ||
                s.name.toLowerCase().includes(term) ||
                (s.serviceCategoryName || '').toLowerCase().includes(term)
            );
        }
        this.serviceCurrentPage = 1;
    }

    addService(service: ServiceResponse) {
        if (!this.checkDetractionCompatibility(service.detractionId)) {
            this.showServiceModal = false;
            return;
        }
        const price = this.currencyCode === 'USD'
            ? (service.priceUsd ?? 0)
            : (service.pricePen ?? 0);
        this.items.push({
            itemType: 'SERVICIO',
            serviceId: service.id,
            description: service.name,
            quantity: 1,
            unitPrice: price,
            _detractionId: service.detractionId ?? undefined,
            _detractionCode: service.detractionCode ?? undefined,
            _detractionDescription: service.detractionDescription ?? undefined,
            _detractionPercentage: service.detractionPercentage ?? undefined,
            _detractionMinAmount: service.detractionMinAmount ?? undefined,
        });
        this.showServiceModal = false;
        this.notify.success(`"${service.name}" agregado`, 'Servicio agregado', 2500);
    }

    servicePrevPage() { if (this.serviceCurrentPage > 1) this.serviceCurrentPage--; }
    serviceNextPage() { if (this.serviceCurrentPage < this.serviceTotalPages) this.serviceCurrentPage++; }

    // =========================================================
    // SERVICIO RÁPIDO
    // =========================================================

    openQuickServiceModal() {
        this.showQuickServiceModal = true;
        this.quickServiceName = '';
        this.quickServiceQuantity = 1;
        this.quickServicePrice = 0;
        this.quickServiceDiscount = 0;
        this.quickServiceDetractionId = '';
        this.loadDetractionCodes();
    }

    submitQuickService() {
        if (!this.quickServiceName.trim()) {
            this.notify.warning('El nombre del servicio es requerido');
            return;
        }
        if (!this.quickServicePrice || this.quickServicePrice <= 0) {
            this.notify.warning('El precio debe ser mayor a 0');
            return;
        }
        const selectedDetraction = this.quickServiceDetractionId
            ? this.allDetractionCodes.find(d => String(d.id) === this.quickServiceDetractionId)
            : undefined;

        if (!this.checkDetractionCompatibility(selectedDetraction?.id)) {
            this.showQuickServiceModal = false;
            return;
        }

        this.items.push({
            itemType: 'PERSONALIZADO',
            description: this.quickServiceName.trim(),
            quantity: this.quickServiceQuantity,
            unitPrice: this.quickServicePrice,
            discountPercentage: this.quickServiceDiscount || undefined,
            detractionCodeId: selectedDetraction?.id ?? undefined,
            _detractionId: selectedDetraction?.id ?? undefined,
            _detractionCode: selectedDetraction?.code ?? undefined,
            _detractionDescription: selectedDetraction?.description ?? undefined,
            _detractionPercentage: selectedDetraction?.percentage ?? undefined,
            _detractionMinAmount: selectedDetraction?.minAmount ?? undefined,
        });
        this.showQuickServiceModal = false;
        this.notify.success(`"${this.quickServiceName.trim()}" agregado`, 'Servicio rápido agregado', 2500);
    }

    addQuickService(name: string, price: number) {
        if (!name || !price) return;
        this.items.push({
            itemType: 'PERSONALIZADO',
            description: name,
            quantity: 1,
            unitPrice: price
        });
    }

    removeItem(index: number) {
        this.items.splice(index, 1);
    }

    // =========================================================
    // PAGOS
    // =========================================================

    addPayment() {
        this.payments.push({
            paymentMethodId: '6',
            amountPaid: 0,
            paymentReference: ''
        });
    }

    removePayment(index: number) {
        this.payments.splice(index, 1);
    }

    // =========================================================
    // CUOTAS
    // =========================================================

    addInstallment() {
        const n = this.installments.length + 1;
        this.installments.push({
            installmentNumber: n,
            dueDate: '',
            amount: 0
        });
    }

    removeInstallment(index: number) {
        this.installments.splice(index, 1);
        this.installments.forEach((inst, i) => inst.installmentNumber = i + 1);
    }

    get installmentsTotal(): number {
        return this.installments.reduce((sum, i) => sum + Number(i.amount || 0), 0);
    }

    get installmentsDiff(): number {
        return this.installmentsTotal - this.paymentTarget;
    }

    get installmentsBalanced(): boolean {
        return Math.abs(this.installmentsDiff) <= 0.01;
    }

    onPaymentFileChange(index: number, event: Event) {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
            this.payments[index].file = file;
        }
    }

    // =========================================================
    // TOTALES
    // =========================================================

    // Subtotal = suma de items SIN IGV (precios base)
    get subtotal(): number {
        return this.items.reduce((sum, i) => {
            const discount = (i.discountPercentage || 0) / 100;
            return sum + (i.quantity * i.unitPrice * (1 - discount));
        }, 0);
    }

    // IGV = 18% sobre el subtotal
    get igv(): number {
        return this.subtotal * 0.18;
    }

    // Total = subtotal + IGV
    get total(): number {
        return this.subtotal + this.igv;
    }

    // =============================
    // DETRACCIÓN (getters y métodos)
    // =============================

    get activeDetraction(): { id: number; code: string; description: string; percentage: number; minAmount: number } | null {
        const item = this.items.find(i => i._detractionId);
        if (!item) return null;
        return {
            id: item._detractionId!,
            code: item._detractionCode!,
            description: item._detractionDescription!,
            percentage: item._detractionPercentage!,
            minAmount: item._detractionMinAmount ?? 700
        };
    }

    get activeDetractionSourceType(): string | null {
        return this.items.find(i => i._detractionId)?.itemType ?? null;
    }

    get detractionMinAmount(): number {
        return this.activeDetraction?.minAmount ?? 700;
    }

    get detractionCondition(): string {
        if (this.items.length === 0) return 'No evaluado';
        if (!this.activeDetraction) return 'No sujeto';
        return this.totalInPen > this.detractionMinAmount
            ? 'Sujeto a detracción'
            : `No aplica (min. S/ ${this.detractionMinAmount.toFixed(2)})`;
    }

    /** La detracción se aplica efectivamente solo si hay código Y el monto supera el mínimo del código */
    get detractionEffective(): boolean {
        return !!this.activeDetraction && this.totalInPen > this.detractionMinAmount;
    }

    // Detracción: round to nearest integer, 0.5 rounds UP (14.49→14, 14.50→15)
    private roundDetraction(value: number): number {
        return Math.round(value);
    }

    get detractionAmount(): number {
        if (!this.detractionEffective) return 0;
        return Math.round(this.total * (this.activeDetraction!.percentage / 100) * 100) / 100;
    }

    // Siempre en PEN para mostrar en el bloque (convierte si la venta es en USD)
    get detractionAmountPen(): number {
        if (!this.detractionEffective) return 0;
        if (this.currencyCode === 'USD' && this.exchangeRateSale > 0) {
            return this.roundDetraction(this.detractionAmount * this.exchangeRateSale);
        }
        return this.roundDetraction(this.detractionAmount);
    }

    get detractionNetAmount(): number {
        return this.total - this.detractionAmount;
    }

    get canAddProduct(): boolean {
        const src = this.activeDetractionSourceType;
        return src === null || src === 'PRODUCTO';
    }

    get canAddService(): boolean {
        const src = this.activeDetractionSourceType;
        return src === null || src === 'SERVICIO' || src === 'PERSONALIZADO';
    }

    private checkDetractionCompatibility(detractionId: number | undefined): boolean {
        const active = this.activeDetraction;

        if (!active && !detractionId) return true;

        if (!active && detractionId) {
            if (this.items.some(i => !i._detractionId)) {
                this.notify.warning('No puedes mezclar ítems con y sin detracción en la misma venta');
                return false;
            }
            return true;
        }

        if (active && !detractionId) {
            this.notify.warning(`Esta venta es sujeta a detracción (${active.code}). Solo puedes agregar ítems con ese mismo código.`);
            return false;
        }

        if (active && detractionId !== active.id) {
            this.notify.warning(`Solo puedes agregar ítems con el código ${active.code} — ${active.description}`);
            return false;
        }

        return true;
    }

    loadDetractionCodes(): void {
        if (this.allDetractionCodes.length > 0) {
            this.buildDetractionOptions();
            return;
        }
        this.loadingDetractionCodes = true;
        this.detractionCodeService.getAll({ category: 'SERVICIO', status: 1 }).subscribe({
            next: res => {
                this.allDetractionCodes = res?.data ?? [];
                this.buildDetractionOptions();
                this.loadingDetractionCodes = false;
            },
            error: () => {
                this.allDetractionCodes = [];
                this.buildDetractionOptions();
                this.loadingDetractionCodes = false;
            }
        });
    }

    private buildDetractionOptions(): void {
        this.detractionCodeOptions = [
            { value: '', label: 'Sin detracción' },
            ...this.allDetractionCodes.map(d => ({
                value: String(d.id),
                label: `${d.code} - ${d.description} (${d.percentage}%)`
            }))
        ];
    }

    // =============================
    // RETENCIÓN IGV
    // =============================
    readonly RETENTION_RATE = 0.03;

    /** Total de la venta convertido a soles (para validar umbrales de 700 PEN) */
    get totalInPen(): number {
        if (this.currencyCode === 'USD' && this.exchangeRateSale > 0) {
            return Math.round(this.total * this.exchangeRateSale * 100) / 100;
        }
        return this.total;
    }

    get clientIsRetentionAgent(): boolean {
        return this.selectedClient?.retentionAgent === true;
    }

    get isFactura(): boolean {
        return this.documentTypeCode === '01';
    }

    get retentionApplies(): boolean {
        // Detracción tiene prioridad; ambos impuestos no pueden coexistir
        if (this.activeDetraction) return false;
        // Umbral siempre en soles: si la venta es en USD se convierte con el TC del día
        return this.clientIsRetentionAgent && this.isFactura && this.totalInPen > 700;
    }

    get retentionAmount(): number {
        // HALF_UP 2 decimals in sale currency
        return this.retentionApplies ? Math.floor(this.total * this.RETENTION_RATE * 100 + 0.5) / 100 : 0;
    }

    /** Retención siempre en PEN. USD: convierte total→PEN antes de aplicar tasa */
    get retentionAmountPen(): number {
        if (!this.retentionApplies) return 0;
        if (this.currencyCode === 'USD' && this.exchangeRateSale > 0) {
            return Math.floor(this.total * this.exchangeRateSale * this.RETENTION_RATE * 100 + 0.5) / 100;
        }
        return this.retentionAmount;
    }

    get netAmount(): number {
        return this.total - this.retentionAmount;
    }

    get netAmountPen(): number {
        if (!this.retentionApplies) return 0;
        if (this.currencyCode === 'USD' && this.exchangeRateSale > 0) {
            return Math.round(this.netAmount * this.exchangeRateSale * 100) / 100;
        }
        return this.netAmount;
    }

    // target amount for payments/installments
    get paymentTarget(): number {
        let target = this.total;
        if (this.retentionApplies) target -= this.retentionAmount;
        if (this.detractionEffective) target -= this.detractionAmount;
        return Math.round(target * 100) / 100;
    }

    get totalPaid(): number {
        return this.payments.reduce((sum, p) =>
            sum + Number(p.amountPaid || 0), 0
        );
    }

    get change(): number {
        return this.totalPaid - this.paymentTarget;
    }

    // =========================================================
    // VALIDACIONES
    // =========================================================

    private validateBeforeSubmit(finalize: boolean): string | null {

        if (!this.selectedClient)
            return 'Debe seleccionar un cliente';

        if (!this.clientAddress.trim())
            return 'Debe consignar la dirección del cliente';

        if (this.items.length === 0)
            return 'Debe agregar al menos un item';

        if (finalize) {

            if (this.isFactura && !this.otNumber.trim())
                return 'La OT IDMH es obligatoria para emitir una Factura';

            if (!this.documentSeriesId)
                return 'Debe seleccionar serie del comprobante';

            if (this.paymentType === 'CREDITO') {
                if (this.installments.length === 0)
                    return 'Debe registrar al menos una cuota';
                if (this.installments.some(i => !i.dueDate))
                    return 'Todas las cuotas deben tener fecha de vencimiento';
                if (this.installments.some(i => !(i.amount > 0)))
                    return 'El monto de cada cuota debe ser mayor a 0';
                if (!this.installmentsBalanced) {
                    const target = (this.retentionApplies || this.detractionEffective) ? 'monto neto' : 'total';
                    return `La suma de cuotas (${this.currencySymbol} ${this.installmentsTotal.toFixed(2)}) debe ser igual al ${target}`;
                }
            } else {
                if (this.payments.length === 0)
                    return 'Debe registrar al menos un pago';
                if (this.totalPaid < this.paymentTarget)
                    return `El total pagado es menor al ${(this.retentionApplies || this.detractionEffective) ? 'monto neto' : 'total'} de la venta`;
            }
        }

        return null;
    }

    // =========================================================
    // GENERATE QUOTATION
    // =========================================================

    onGenerateQuotation(): void {
        const error = this.validateBeforeSubmit(false);
        if (error) {
            this.notify.warning(error, 'Validación');
            return;
        }

        this.isGeneratingQuotation = true;

        const request: SaleRequest = {
            clientId: this.selectedClient!.id,
            clientAddressId: this.selectedClientAddressId ?? undefined,
            clientAddress: this.clientAddress.trim(),
            items: this.items,
        };

        this.saleService.generateQuotation(request).subscribe({
            next: blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const client = this.selectedClient!;
                const clientName = client.businessName?.trim()
                    || `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim();
                a.download = `Cotización - ${clientName}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                this.isGeneratingQuotation = false;
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al generar cotización');
                this.isGeneratingQuotation = false;
            }
        });
    }

    // =========================================================
    // SUBMIT
    // =========================================================

    submitSale(finalize: boolean) {

        const error = this.validateBeforeSubmit(finalize);
        if (error) {
            this.notify.warning(error, 'Validación');
            return;
        }

        this.isSubmitting = true;

        const request: SaleRequest = {
            clientId: this.selectedClient!.id,
            clientAddressId: this.selectedClientAddressId ?? undefined,
            clientAddress: this.clientAddress.trim(),
            items: this.items.map(i => ({
                itemType: i.itemType,
                productId: i.productId,
                serviceId: i.serviceId,
                description: i.description,
                quantity: i.quantity,
                unitPrice: i.unitPrice,
                discountPercentage: i.discountPercentage,
                detractionCodeId: i.detractionCodeId,
            })),
            currencyCode: this.currencyCode,
            paymentType: this.paymentType,
            purchaseOrder: this.purchaseOrder.trim() || undefined,
            observations: this.observations.trim() || undefined,
            otNumber: this.otNumber.trim() || undefined,
            relatedGuides: this.relatedGuides.filter(g => g.trim()).map(g => g.trim()),
            payments: [],
            installments: this.paymentType === 'CREDITO'
                ? this.installments.map(i => ({
                    installmentNumber: i.installmentNumber,
                    dueDate: String(i.dueDate),
                    amount: Number(i.amount)
                }))
                : undefined,
            draft: !finalize,
            documentSeriesId: finalize && this.documentSeriesId > 0 ? this.documentSeriesId : undefined
        };

        const paymentFiles: { proofKey: string; file: File }[] = [];

        if (this.paymentType === 'CONTADO') {
            this.payments.forEach((p, index) => {

                const payment: any = {
                    paymentMethodId: Number(p.paymentMethodId),
                    amountPaid: p.amountPaid,
                    paymentReference: p.paymentReference
                };

                // Solo si no es efectivo y tiene archivo
                if (p.paymentMethodId !== '6' && p.file) {
                    const proofKey = `proof_${index}_${Date.now()}`;
                    payment.proofKey = proofKey;

                    paymentFiles.push({
                        proofKey,
                        file: p.file
                    });
                }

                request.payments?.push(payment);
            });
        }

        const request$ = this.selectedSale?.saleStatus === 'BORRADOR'
            ? this.saleService.updateDraft(this.selectedSale.id, request, paymentFiles)
            : this.saleService.create(request, paymentFiles);

        request$.subscribe({
            next: () => {
                this.notify.success(
                    finalize ? 'Venta emitida correctamente' : 'Borrador guardado correctamente',
                    'Éxito'
                );
                this.resetSale();
                this.isSubmitting = false;
                this.onSaved.emit();
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al registrar venta');
                this.isSubmitting = false;
            }
        });
    }

    // =========================================================
    // RESET
    // =========================================================

    resetSale() {
        this.selectedClient = undefined;
        this.selectedClientAddressId = null;
        this.clientAddress = '';
        this.items = [];
        this.payments = [];
        this.installments = [];
        this.purchaseOrder = '';
        this.observations = '';
        this.otNumber = '';
        this.relatedGuides = [];
        this.currencyCode = 'PEN';
        this.paymentType = 'CONTADO';
        this.documentSeriesId = 0;
        this.isViewMode = false;
        this.loadingDetail = false;
        this.documentTypeLocked = false;
        this.documentTypeCode = '03';
        this.allProducts = [];
        this.allServices = [];
        this.allDetractionCodes = [];
        this.detractionCodeOptions = [];
    }

    getViewPaymentTotal(): number {
        return (this.selectedSale?.payments ?? []).reduce((sum, p) => sum + p.amountPaid, 0);
    }

    getViewInstallmentTotal(): number {
        return (this.selectedSale?.installments ?? []).reduce((sum, i) => sum + i.amount, 0);
    }

    closeModal() {
        this.onClose.emit();
    }
}
